import { Refine } from "@refinedev/core";
import {
  useNotificationProvider,
  RefineThemes,
} from "@refinedev/antd";
import { useMatch } from "react-router-dom";
import routerProvider, {
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from "@refinedev/react-router-v6";

import { App as AntdApp, ConfigProvider } from "antd";
import "@refinedev/antd/dist/reset.css";
import { SAAS_ADMIN, TENANT_ADMIN, TENANT_USER, useAuthContext } from "./providers/auth-provider";

import { useTranslation } from "react-i18next";
import { can } from "./providers/access-control-provider";
import { ThemeProvider } from "antd-style";
import { dataProvider } from "./providers/data-provider";
import { OTHER_RESOURCES, RoutesConfig, TENANT_RESOURCES, K8S_RESOURCES, TENANT_USER_RESOURCES } from "./Routes";
import { Link } from "./components/common/Link";
import { UrlParser } from "./utils/urlParser";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect } from "react";
import { useResourceRootContext } from "./layouts/ResourceContext";

function App() {
  const { authProvider, user } = useAuthContext();
  const match = useMatch('t/:tenantName/*');
  const matchK8s = useMatch('k8s/:id/*');
  const { t, i18n } = useTranslation();
  const { state, dispatch } = useResourceRootContext();
  
  useEffect(() => {
    if (match) {
      if (state.tenantName !== match.params.tenantName) {
        dispatch({ type: 'TENANT_MODE', payload: { tenantName: match.params.tenantName } });
      }
    } else if (state.tenantName) {
      dispatch({ type: 'TENANT_MODE', payload: false });
    }
  }, [match]);

  const i18nProvider = {
    translate: (key, params) => t(key, params),
    changeLocale: (lang) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  const accessControlProvider = {
    can: (params) => {
      return can(authProvider, params);
    },
  };

  let resources = [{
    name: "home",
    list: "/",
  }];
  if ( user && user.is === SAAS_ADMIN) {
    if (match) { // saas admin tenant mode
      resources = [
        ...resources,
        ...TENANT_RESOURCES(t),
      ];
    } else if(matchK8s){
      resources = [
        ...resources,
        ...K8S_RESOURCES(t),
      ];
    } else { // saas admin normal mode
      const ores = OTHER_RESOURCES(t);
      if (import.meta.env.DEV) {
        ores.push({
          name: "dev",
          list: "/dev",
          meta: {
            label: "Development",
          },
        });
      }
      resources = [
        ...resources,
        ...ores,
      ];
    }
  } else if (user && user.is == TENANT_ADMIN) { // tenant admin
    resources = [
      ...resources,
      ...TENANT_RESOURCES(t),
    ];
    if(!state.tenantMode) dispatch({ type: 'TENANT_MODE', payload: { tenantName: user.tenant } });
  } else if (user && user.is == TENANT_USER) {
    resources = [
      ...resources,
      ...TENANT_USER_RESOURCES(t),
    ];
  }

  const lightTheme = {
    ...RefineThemes.Blue,
    token: {
      colorPrimary: '#1385c1',
      fontFamily: 'Oxygen, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
  };

  const darkTheme = {
    ...RefineThemes.Dark,
    token: {
      colorPrimary: '#FAAD14',
      colorLink: '#ffd200',
      fontFamily: 'Oxygen, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
  };

  return (
    <ThemeProvider theme={(appearance) => {
      if (appearance === "dark") {
        return darkTheme;
      } else {
        return lightTheme;
      }
    }}>
      <ConfigProvider locale={{ locale: i18n.language }}>
        <AntdApp>
          <Refine
            authProvider={authProvider}
            routerProvider={{ ...routerProvider, parse: UrlParser, Link: Link }}
            accessControlProvider={accessControlProvider}
            dataProvider={dataProvider}
            notificationProvider={useNotificationProvider}
            i18nProvider={i18nProvider}
            options={{
              syncWithLocation: true, 
              warnWhenUnsavedChanges: true,
            }}
            resources={resources}
          >
            <RoutesConfig />
            <UnsavedChangesNotifier />
            <DocumentTitleHandler />
            <ReactQueryDevtools initialIsOpen={false} />
          </Refine>
        </AntdApp>
      </ConfigProvider>
    </ThemeProvider>
  );
}

export default App;
